import { useCallback } from 'react';
import _ from 'lodash';
import { IFeedSpread, DataFeedSubscription } from '@tradingblock/types';
import { useLog } from '@tradingblock/components';
import {
  spreadEquals,
} from '../utils/subscriptionHelpers';

const log = useLog('DataFeedInitializer');

/**
 * Handles initial subscription when feed opens
 */
export const useInitialSubscriptionHandler = (
  accountId: number,
  subscribeQuote: (accountId: string | number, sub: DataFeedSubscription | DataFeedSubscription[]) => boolean
) => {
  return useCallback(<T extends string | IFeedSpread>(
    feedJustOpened: boolean,
    hasInitialized: React.MutableRefObject<boolean> | { current: boolean; setCurrent: (value: boolean) => void },
    items: T[],
    subscription: DataFeedSubscription | DataFeedSubscription[],
    setSubscribed: (items: T[]) => void,
    feedType: 'quotes' | 'spreads'
  ) => {
    if (feedJustOpened) {
      // Support both ref and state-based initialization
      if ('setCurrent' in hasInitialized) {
        hasInitialized.setCurrent(false);
      } else {
        hasInitialized.current = false;
      }
      
      const itemsArray = Array.isArray(items) ? items : [];

      if (itemsArray.length > 0) {
        log(`${feedType} feed opened, subscribing to all current ${feedType} %o`, itemsArray);
        const subscribeResult = subscribeQuote(accountId, subscription);
        if (subscribeResult) {
          setSubscribed(itemsArray);
          if ('setCurrent' in hasInitialized) {
            hasInitialized.setCurrent(true);
          } else {
            hasInitialized.current = true;
          }
        }
      } else {
        setSubscribed([] as T[]);
        if ('setCurrent' in hasInitialized) {
          hasInitialized.setCurrent(true);
        } else {
          hasInitialized.current = true;
        }
      }
    }
  }, [accountId, subscribeQuote]);
};

/**
 * Handles incremental subscription updates (add/remove)
 */
export const useIncrementalUpdateHandler = (
  accountId: number,
  subscribeQuote: (accountId: string | number, sub: DataFeedSubscription | DataFeedSubscription[]) => boolean,
  unsubscribeQuote: (accountId: string | number, sub: DataFeedSubscription | DataFeedSubscription[]) => boolean
) => {
  return useCallback(<T extends string | IFeedSpread>(
    isOpen: boolean,
    hasInitialized: boolean,
    changes: { toAdd: T[]; toRemove: T[] },
    subscribed: T[],
    subscriptionFactory: (items: T[]) => DataFeedSubscription | DataFeedSubscription[],
    setSubscribed: (items: T[]) => void,
    feedType: 'quotes' | 'spreads',
    compareFn?: (a: T, b: T) => boolean
  ) => {
    const { toAdd = [], toRemove = [] } = changes || {};
    
    if (!isOpen || !hasInitialized) {
      return;
    }

    // Filter toAdd to only include items not already subscribed
    const actuallyNeedToAdd: T[] = (feedType === 'quotes'
      ? (toAdd as string[]).filter(symbol => !(subscribed as string[]).includes(symbol))
      : (toAdd as IFeedSpread[]).filter(spread => {
          const isAlreadySubscribed = (subscribed as IFeedSpread[]).some(sub => {
            if (compareFn) {
              return compareFn(spread as T, sub as T);
            }
            return spreadEquals(spread, sub);
          });
          return !isAlreadySubscribed;
        })) as T[];
    
    // Filter toRemove to only include items actually subscribed
    const actuallyNeedToRemove: T[] = (feedType === 'quotes'
      ? (toRemove as string[]).filter(symbol => (subscribed as string[]).includes(symbol))
      : (toRemove as IFeedSpread[]).filter(toRemoveSpread => {
          const isSubscribed = (subscribed as IFeedSpread[]).some(sub => {
            if (compareFn) {
              return compareFn(toRemoveSpread as T, sub as T);
            }
            return spreadEquals(toRemoveSpread, sub);
          });
          return isSubscribed;
        })) as T[];

    if (actuallyNeedToAdd.length === 0 && actuallyNeedToRemove.length === 0) {
      return;
    }

    let updated = subscribed;
    let hasChanges = false;

    if (actuallyNeedToAdd.length > 0) {
      const subscription = subscriptionFactory(actuallyNeedToAdd);
      const result = subscribeQuote(accountId, subscription);
      if (result) {
        updated = feedType === 'quotes'
          ? _.uniq([...updated as string[], ...(actuallyNeedToAdd as string[])]) as T[]
          : [...updated, ...actuallyNeedToAdd] as T[];
        log(`subscribed to ${feedType} %o`, actuallyNeedToAdd);
        hasChanges = true;
      } else {
        log(`failed to subscribe to ${feedType} %o`, actuallyNeedToAdd);
      }
    }

    if (actuallyNeedToRemove.length > 0) {
      const subscription = subscriptionFactory(actuallyNeedToRemove);
      const result = unsubscribeQuote(accountId, subscription);
      if (result) {
        updated = feedType === 'quotes'
          ? (updated as string[]).filter(q => !(actuallyNeedToRemove as string[]).includes(q)) as T[]
          : (updated as IFeedSpread[]).filter(sub => !(actuallyNeedToRemove as IFeedSpread[]).some(toRemove => spreadEquals(toRemove, sub))) as T[];
        log(`unsubscribed from ${feedType} %o`, actuallyNeedToRemove);
        hasChanges = true;
      } else {
        log(`failed to unsubscribe from ${feedType} %o`, actuallyNeedToRemove);
      }
    }

    if (hasChanges) {
      setSubscribed(updated);
    }
  }, [accountId, subscribeQuote, unsubscribeQuote]);
};

